/**
 * What each form looks like, and which fields the clinic insists on.
 *
 * The collection schema in `content/` says what a field *is* — its type, its
 * options, whether Webflow called it required. It does not say what to call it
 * on screen, what order to ask for things in, or which fields the clinic has
 * decided a published Provider must have. That is here.
 *
 * Kept as data, and shared by the browser and the API, so the form somebody
 * fills in and the rules the server applies are the same list. A field the form
 * shows but the server does not know about is how a value reaches `fieldData`
 * unchecked.
 *
 * ## Labels
 *
 * Deliberately not the schema's `displayName`. Webflow's are the names of
 * database columns as somebody typed them in 2023 — "Assesments(s)", "Group",
 * "Meta Description [SEO]" — and CONTEXT.md has words for these things that the
 * clinic uses out loud. A Provider belongs to a **Discipline**, not a Group.
 */

// The build's own vocabulary for a review and a question (ADR-0010): the rules
// below say the same things the build's twins say, in the form's words.
import { PHOTO_CONSENT_SCOPE, SHORT_QUOTE_CHARS, bookingMessage, bookingSentences, isLive, normaliseQuestion, optionName, outcomeClaims, plainText } from "../../lib/content-rules.mjs";
import { formatDay } from "./dates.js";

/**
 * Fields a published item must have filled in, beyond what the schema marks
 * required (which is only `name` and `slug`).
 *
 * The brief's §9.6 asked the clinic which fields to insist on and recommended
 * these four for a Provider; that recommendation is what is implemented, and it
 * is one line to change. They apply to a **published** item only — a draft can
 * be as unfinished as it likes, which is the point of a draft.
 */
export const MANDATORY = {
  provider: ["specialty", "background", "about", "meta-description-seo"],
  article: ["article-h1-title", "description", "content"],
  author: ["bio-summary"],
  // Brief §4.1: the consent record is part of what makes a review publishable,
  // and `reviewer-unrelated` is a RULES error because a Switch cannot be
  // mandatory (false counts as filled).
  testimonials: ["client-name", "testimonial-content", "rating", "source", "consent-on", "consent-scope", "consent-by"],
  faq: ["content", "category"],
  // Brief §6.1: the wage is the Pay Transparency Act; the date and the mailbox
  // are what the page prints.
  careers: ["short-description", "wage", "main-duties", "requirements", "published-on", "apply-email"],
};

/**
 * @typedef {object} Rule
 * @property {string} field            the field the message belongs under
 * @property {"warning"|"error"} level an error blocks Publish; a warning never does
 * @property {(value: any, fieldData: object, refs: object) => boolean} test
 *   true when the Item is fine; false produces the finding. `refs` is the
 *   reference index — `byId`, `collections` — so a rule can look at the Item
 *   something points to.
 * @property {string|((value: any, fieldData: object, refs?: object) => string)} message
 */

/**
 * The clinic's own checks beyond "is it filled in", as data, per collection.
 *
 * Judged on publish only, like `MANDATORY`, and by the server (`validateItem`
 * in content.js). A rule that must also hold for a hand edit needs a twin in
 * `lib/content-rules.mjs`: the panel cannot be more permissive than the build
 * (ADR-0010), but a rule that lives only here can be walked around with a git
 * commit, so it should say something about finish, not about correctness.
 *
 * @type {Record<string, Rule[]>}
 */
export const RULES = {
  // Clients request an assessment; they never book or choose a provider (the
  // owner, 2026-10-13; ADR-0026). The bio and the meta description are the
  // clinic's text, so a sentence that asks the reader to book is a warning,
  // here and in tools/check-content.mjs (lib/content-rules.mjs), never a refusal.
  provider: ["about", "meta-description-seo"].map((field) => ({
    field,
    level: "warning",
    test: (value) => bookingSentences(value).length === 0,
    message: (value) => bookingMessage(field, bookingSentences(value)),
  })),
  article: [],
  author: [],

  testimonials: [
    {
      field: "reviewer-unrelated",
      level: "error",
      test: (value) => value === true,
      message: "Confirm that the reviewer is not an employee or relative of the clinic before publishing (Competition Act; Ad Standards).",
    },
    {
      field: "users-image",
      level: "error",
      test: (value, data, refs) => !hasPicture(value) || scopeName(data, refs) === PHOTO_CONSENT_SCOPE,
      message: `A photo can only be shown when the consent scope is “${PHOTO_CONSENT_SCOPE}”. Remove the picture, or record that the reviewer agreed to it.`,
    },
    {
      field: "client-name",
      level: "warning",
      test: (value, data, refs) => !(fullWords(value) >= 2 && SHORT_NAME_SCOPES.has(scopeName(data, refs))),
      message: (value) => `“${String(value).trim()}” looks like a full name, but the reviewer agreed to less than that. Show only what they agreed to — a first name and an initial, say.`,
    },
    {
      field: "source",
      level: "error",
      test: (value, data, refs) => sourceName(data, refs) !== "Google review" || Boolean(String(data["reviews-link-2"] || "").trim()),
      message: "A Google review needs its link, so a reader — or a college — can find the original.",
    },
    {
      // The page prints the value as an `href`, verbatim; a `javascript:` or
      // `data:` address there runs on the clinic's origin, and Nunjucks'
      // escaping does nothing about a scheme. The build's twin is pending
      // (ADR-0016); the panel may be stricter than the build (ADR-0010).
      field: "reviews-link-2",
      level: "error",
      test: (value) => !String(value || "").trim() || HTTPS_URL.test(String(value).trim()),
      message: "The link must start with https://. It is printed as a link on /testimonials, and anything else is not a web address.",
    },
    {
      field: "reviews-link-2",
      level: "warning",
      test: (value) => !String(value || "").trim() || GOOGLE_HOSTS.has(hostOf(value)),
      message: (value) => `The link goes to ${hostOf(value) || "somewhere unreadable"}, not to Google (g.co, google.com or maps.app.goo.gl). Check it is the review itself.`,
    },
    {
      // Judged by the Vancouver day, not the instant: the day control stores
      // a consent date as 23:59:59 Pacific of that day (dates.js), so consent
      // given this morning is an instant still to come until midnight.
      field: "consent-on",
      level: "warning",
      test: (value) => !value || Number.isNaN(Date.parse(value)) || formatDay(value) <= formatDay(new Date().toISOString()),
      message: "The consent date is in the future. It should be the day the reviewer agreed, in the past.",
    },
    {
      field: "testimonial-content",
      level: "warning",
      test: (value) => plainText(value).length >= SHORT_QUOTE_CHARS,
      message: (value) => `The quote is ${plainText(value).length} characters — a rating rather than a review. It publishes as written; worth a look.`,
    },
    {
      field: "testimonial-content",
      level: "warning",
      test: (value) => outcomeClaims(plainText(value)).length === 0,
      message: (value) =>
        `The quote says ${outcomeClaims(plainText(value)).map((w) => `“${w}”`).join(", ")}. A review must not promise an outcome or a guarantee (CHCPBC standard 1.6; CPSBC advertising standard) — read the sentence, and if it is a claim, do not publish it as written.`,
    },
  ],

  faq: [
    {
      field: "assessment",
      level: "warning",
      test: (value, data, refs) => categoryName(data, refs) !== "Assessment" || attached(value),
      message: "An Assessment question with no assessment attached will appear on /questions only.",
    },
    {
      field: "service",
      level: "warning",
      test: (value, data, refs) => categoryName(data, refs) !== "Service" || attached(value),
      message: "A Service question with no service attached will appear on /questions only.",
    },
    {
      field: "category",
      level: "warning",
      test: (value, data, refs) => !["General", "Counselling Page"].includes(categoryName(data, refs)) || !(attached(data.assessment) || attached(data.service)),
      message:
        "A General or Counselling question goes by its kind, not by what is attached: an attached assessment is ignored, and an attached service page shows it as well. Check the kind.",
    },
    {
      field: "name",
      level: "warning",
      test: (value, data, refs) => !duplicateQuestion(value, data, refs),
      message: (value, data, refs) => {
        const other = duplicateQuestion(value, data, refs);
        return `Another live question asks the same thing: “${other?.fieldData?.name}” (${other?.fieldData?.slug}). Attach that one to this page and archive this copy, so the answer is written once.`;
      },
    },
  ],

  careers: [
    {
      field: "noc-code",
      level: "warning",
      test: (value) => !String(value || "").trim() || /^\d{5}/.test(String(value).trim()),
      message: "A NOC 2021 code starts with five digits — “13100”, say. Check it against noc.esdc.gc.ca.",
    },
    {
      field: "wage",
      level: "warning",
      test: (value) => !String(value || "").trim() || /\$\s?\d/.test(String(value)),
      message: "The wage has no dollar figure. BC's Pay Transparency Act asks for the expected wage or range — “$28.00–$32.00/hr”, say.",
    },
    {
      field: "closing-date",
      level: "warning",
      test: (value, data) => !value || !data["published-on"] || Date.parse(value) >= Date.parse(data["published-on"]),
      message: "Applications would close before the posting appears: the closing date is before the posting date.",
    },
    {
      field: "apply-email",
      level: "warning",
      test: (value) => !String(value || "").trim() || /@allbrainsclinic\.com$/i.test(String(value).trim()),
      message: "The applications mailbox is not an @allbrainsclinic.com address. Applications would go outside the clinic.",
    },
  ],
};

/* ------------------------------------------- what the rules above lean on */

/** A full web address: the only kind a page may print as a link. */
const HTTPS_URL = /^https:\/\/\S+$/;

/** The hosts a Google review permalink can be on. */
const GOOGLE_HOSTS = new Set(["g.co", "google.com", "www.google.com", "maps.app.goo.gl", "maps.google.com"]);

/** The consent scopes under which a full name must not be shown. */
const SHORT_NAME_SCOPES = new Set(["First name and initial", "Anonymous"]);

const hasPicture = (value) => Boolean(value && typeof value === "object" && value.url);
/**
 * The words of a name that are more than an initial: "Sarah M." is one,
 * "Sarah Miller" is two. An initial is one letter, with or without its stop.
 */
const fullWords = (value) =>
  String(value || "")
    .trim()
    .split(/\s+/)
    .filter((word) => word.replace(/[.\u2019']/g, "").length > 1).length;
const attached = (value) => Array.isArray(value) && value.length > 0;

const hostOf = (url) => {
  try {
    return new URL(String(url)).hostname.toLowerCase();
  } catch {
    return "";
  }
};

/** An Option field's stored id as its name, read off the schema the reference index carries. */
function nameOf(refs, collection, field, value) {
  const schema = refs?.collections?.[collection]?.collection;
  return optionName((schema?.fields || []).find((f) => f.slug === field), value);
}
const scopeName = (data, refs) => nameOf(refs, "testimonials", "consent-scope", data["consent-scope"]);
const sourceName = (data, refs) => nameOf(refs, "testimonials", "source", data["source"]);
const categoryName = (data, refs) => nameOf(refs, "faq", "category", data.category);

/** Another Live FAQ asking this question, or null. Its message names the other. */
function duplicateQuestion(value, data, refs) {
  const key = normaliseQuestion(value);
  if (!key) return null;
  const items = refs?.collections?.faq?.items || [];
  return items.find((i) => isLive(i) && i.fieldData?.slug !== data.slug && normaliseQuestion(i.fieldData?.name) === key) || null;
}

/** Field slugs the form never shows, because something else owns them. */
const HIDDEN = new Set(["name", "slug"]);

/**
 * @typedef {object} FormField
 * @property {string} slug
 * @property {string} label
 * @property {string} [help]
 * @property {"text"|"textarea"|"rich"|"image"|"switch"|"number"|"select"|"multiselect"|"date"|"day"|"url"} control
 *   `day` is a date that means a whole day — applications close *on* it — and
 *   is stored as 23:59:59 in Vancouver of the day chosen (shared/dates.js)
 * @property {number} [rows]
 * @property {number} [softLimit] a length past which the editor nudges
 * @property {boolean} [decorative] an Image the template prints with `alt=""`,
 *   so no description is asked for; a photograph of a person is never this
 * @property {{id: number|string, name: string}[]} [options] the choices of a
 *   `select` whose field is not an Option or a Reference — a rating is a
 *   Number that offers 1 to 5. The stored value is the choice's `id`, with
 *   its type kept.
 */

/**
 * @typedef {object} FormSection
 * @property {string} title
 * @property {string} [help]
 * @property {string[]} [intro] lines shown as a checklist above the fields —
 *   what to have in hand before filling the section in
 * @property {boolean} [preview] show the search-result preview in this
 *   section, for a collection whose description lives outside a "Search"
 *   section
 * @property {FormField[]} fields
 */

/** How the stars are offered: a Number, chosen from five. */
const STARS = [5, 4, 3, 2, 1].map((n) => ({ id: n, name: `${n} star${n === 1 ? "" : "s"}` }));

/** The shape of each form, section by section. @type {Record<string, FormSection[]>} */
const FORMS = {
  provider: [
    {
      title: "The person",
      help: "What the page says about them.",
      fields: [
        { slug: "specialty", label: "Role", control: "text", help: "Their job title, under their name on every card and on their page — “Registered Psychologist”." },
        { slug: "background", label: "Credentials", control: "text", help: "The letters after their name, on every card and on their page — “Ph.D.”, “RSLP, S-LP(C)”. Leave empty if they have none: nothing else belongs here." },
        { slug: "card-credentials", label: "Shorter credentials for cards", control: "text", help: "Only when the cards should say less than their page. Every card on the site (Providers, About Us, the home page, the Assessment, Counselling and Service pages) prints this instead of Credentials; their own page keeps Credentials. Leave empty to print Credentials." },
        { slug: "card-role", label: "Shorter role for cards", control: "text", help: "The same, for Role: every card prints this instead; their own page keeps Role. Leave empty to print Role." },
        { slug: "group", label: "Discipline", control: "select", help: "Not printed on any card. It only places someone new on /providers until the team map lists them (content/site/team.json, changed by the website team), which sets every listed person's group, order and the word over their name." },
        { slug: "order", label: "Position in their discipline", control: "number", help: "Orders the Assessment pages' team lists: lower comes first, empty sorts by name. /providers follows the team map (content/site/team.json)." },
        { slug: "photo", label: "Photograph", control: "image", help: "Head and shoulders. The alt text is read aloud to anyone who cannot see it." },
        { slug: "about", label: "About them", control: "rich" },
      ],
    },
    {
      title: "What they do",
      fields: [
        { slug: "service", label: "Main assessment", control: "select", help: "Not shown on their page since October 2026: the assessments a provider page relates to are set in the team map (content/site/team.json), by the website team." },
        { slug: "asses", label: "Other assessments", control: "multiselect", help: "Not shown on their page either; see Main assessment." },
      ],
    },
    {
      title: "Search and sharing",
      help: "How this page looks in Google and when somebody shares the link.",
      fields: [
        { slug: "meta-description-seo", label: "Search description", control: "textarea", rows: 3, softLimit: 160,
          help: "The sentence or two under the link in search results." },
        { slug: "seo-title", label: "Search title", control: "text", softLimit: 60,
          help: "Leave empty for the standard “Name | Provider”." },
        { slug: "og-image", label: "Sharing picture", control: "image", help: "Defaults to the photograph." },
        { slug: "canonical", label: "Canonical address", control: "url", help: "Rarely needed. Names another page as the original of this one." },
        { slug: "noindex", label: "Keep off Google", control: "switch", help: "The page stays published and linked; search engines are asked to skip it." },
      ],
    },
  ],

  article: [
    {
      title: "The article",
      fields: [
        { slug: "article-h1-title", label: "Search title", control: "text", softLimit: 60,
          help: "The <title> in search results. Often longer than the headline on the page." },
        { slug: "description", label: "Summary", control: "textarea", rows: 3, softLimit: 160,
          help: "Shown on /blog and under the link in search results." },
        { slug: "main-image", label: "Header picture", control: "image" },
        { slug: "content", label: "Body", control: "rich" },
      ],
    },
    {
      title: "Publishing",
      fields: [
        { slug: "published-on", label: "Publication date", control: "date",
          help: "What /blog orders by. A date in the future keeps it hidden until then." },
        { slug: "author-2", label: "Author", control: "select" },
        { slug: "featured", label: "Feature on the blog", control: "switch" },
        { slug: "related-articles", label: "Related articles", control: "multiselect" },
        { slug: "video", label: "Video link", control: "url" },
      ],
    },
    {
      title: "Search and sharing",
      fields: [
        { slug: "seo-title", label: "Search title override", control: "text", softLimit: 60,
          help: "Leave empty to use the search title above." },
        { slug: "og-image", label: "Sharing picture", control: "image", help: "Defaults to the header picture." },
        { slug: "canonical", label: "Canonical address", control: "url" },
        { slug: "noindex", label: "Keep off Google", control: "switch" },
      ],
    },
  ],

  author: [
    {
      title: "The author",
      fields: [
        { slug: "bio-summary", label: "One-line biography", control: "textarea", rows: 2 },
        { slug: "picture", label: "Photograph", control: "image" },
        { slug: "bio", label: "Full biography", control: "rich" },
        { slug: "email", label: "Email", control: "text" },
        { slug: "twitter-profile-link", label: "X / Twitter", control: "url" },
        { slug: "facebook-profile-link", label: "Facebook", control: "url" },
      ],
    },
  ],

  testimonials: [
    {
      title: "The review",
      help: "What /testimonials and the home page print. The name at the top of this page follows the reviewer's name here.",
      fields: [
        { slug: "client-name", label: "Reviewer's name, as shown", control: "text",
          help: "Only what they agreed to: a first name and an initial unless the consent scope says more." },
        { slug: "rating", label: "Rating", control: "select", options: STARS, help: "The stars drawn on the page." },
        { slug: "testimonial-content", label: "The review", control: "rich",
          help: "Verbatim. Do not tidy the wording, and do not publish a promise of an outcome." },
        { slug: "source", label: "Where it came from", control: "select" },
        { slug: "reviews-link-2", label: "Link to the original", control: "url",
          help: "The Google permalink (g.co/…), so anyone can find the review itself. Required for a Google review." },
        { slug: "users-image", label: "Photo", control: "image", decorative: true,
          help: "Only with photo consent (“Full name and photo”). Leave empty for the standard placeholder." },
        { slug: "treatment", label: "Services they came for", control: "multiselect" },
        { slug: "assessment", label: "Assessments they came for", control: "multiselect" },
        { slug: "provider", label: "Clinician mentioned (optional)", control: "select" },
      ],
    },
    {
      title: "Consent",
      help: "Nothing here is printed. It is the clinic's record that the reviewer agreed to appear (BC PIPA; CHCPBC and CPSBC advertising standards). Keep the email or form in the clinic's records, not here.",
      intro: [
        "The reviewer's written consent is on file.",
        "The name shown matches the scope they agreed to.",
        "If there is a photo, the consent covers it.",
        "The quote is verbatim.",
        "The quote promises no outcome or guarantee.",
      ],
      fields: [
        { slug: "consent-scope", label: "What they agreed to show", control: "select" },
        { slug: "consent-on", label: "Consent given on", control: "day",
          help: "The day the reviewer agreed in writing to this appearing with the name and photo shown here." },
        { slug: "consent-by", label: "Consent obtained by", control: "text", softLimit: 200,
          help: "Who obtained it, how, and where the evidence is filed — “J. Smith, email 2026-10-02, HR/Reviews folder”. No personal details beyond what the review shows." },
        { slug: "reviewer-unrelated", label: "The reviewer is not an employee or a relative of the clinic", control: "switch",
          help: "Required before publishing (Competition Act; Ad Standards)." },
      ],
    },
  ],

  faq: [
    {
      title: "The answer",
      fields: [
        { slug: "content", label: "Answer", control: "rich",
          help: "Plain, general wording. Never a client's own example, even without a name." },
      ],
    },
    {
      title: "Where it appears",
      help: "A question has no page of its own. Its kind decides which pages show it: Assessment — the attached assessment pages; Service — the attached service pages; Counselling Page — /counselling-psychotherapy; General — /questions. A question no page claims lands on /questions.",
      fields: [
        { slug: "category", label: "Which kind of page", control: "select" },
        { slug: "assessment", label: "Assessment pages", control: "multiselect", help: "For an Assessment question." },
        { slug: "service", label: "Service pages", control: "multiselect",
          help: "New questions appear first on a service page once published." },
      ],
    },
  ],

  careers: [
    {
      title: "Summary",
      preview: true,
      fields: [
        { slug: "short-description", label: "Summary line", control: "textarea", rows: 2, softLimit: 160,
          help: "On the /careers card and under the link in search results." },
      ],
    },
    {
      title: "The posting",
      fields: [
        { slug: "overview", label: "Overview", control: "rich" },
        { slug: "main-duties", label: "Main duties", control: "rich" },
        { slug: "requirements", label: "Requirements", control: "rich" },
      ],
    },
    {
      title: "Facts, as they appear in the side column",
      fields: [
        { slug: "employer", label: "Employer", control: "text", help: "Usually “All Brains Clinic Ltd.”" },
        { slug: "work-location", label: "Work location", control: "text", help: "Usually “Vancouver, BC”." },
        { slug: "wage", label: "Wage or wage range", control: "text",
          help: "Required by BC's Pay Transparency Act — a figure or a range, “$28.00–$32.00/hr”." },
        { slug: "hours", label: "Hours", control: "text", help: "“30 hrs/week”." },
        { slug: "terms", label: "Terms of employment", control: "text", help: "“Permanent, full-time”." },
        { slug: "start-date", label: "Start date", control: "text", help: "“As soon as possible”." },
        { slug: "language", label: "Language of work", control: "text", help: "Usually “English”." },
        { slug: "benefits", label: "Benefits", control: "text" },
        { slug: "noc-code", label: "NOC code", control: "text", help: "The five-digit NOC 2021 code, from noc.esdc.gc.ca." },
      ],
    },
    {
      title: "Dates",
      fields: [
        { slug: "published-on", label: "Date posted", control: "date",
          help: "Usually today. A date in the future keeps the posting hidden until then — same day, typically 2–7 hours after the time, when the site next rebuilds." },
        { slug: "closing-date", label: "Applications close on", control: "day",
          help: "Open through the end of that day in Vancouver. Once it has passed the page says the posting has closed; unpublish it when the position is filled." },
      ],
    },
    {
      title: "Applications",
      fields: [
        { slug: "apply-email", label: "Applications mailbox", control: "text",
          help: "The “Apply by email” button and the how-to-apply copy use it. Usually hr@allbrainsclinic.com." },
      ],
    },
  ],
};

/**
 * Fields the form keeps out of sight for one collection: owned by another
 * screen, not by the editor. A FAQ's `sort` is written by the Reorder card.
 */
const HIDDEN_BY_COLLECTION = { faq: new Set(["sort"]) };

/**
 * The form for a collection, with anything the schema has and the form forgot
 * appended at the end.
 *
 * That last part matters: a field added to the schema and not to this file
 * would otherwise be invisible in the panel and un-editable by the clinic,
 * which is the failure mode of every hand-written form in a schema-driven app.
 * It shows up in a section called "Other", labelled by its own slug, until
 * somebody gives it a proper home here.
 */
export function formFor(collection, schema) {
  const sections = FORMS[collection] || [];
  if (!schema) return sections;

  const placed = new Set(sections.flatMap((s) => s.fields.map((f) => f.slug)));
  const hidden = HIDDEN_BY_COLLECTION[collection] || new Set();
  const missing = (schema.fields || [])
    .filter((f) => !placed.has(f.slug) && !HIDDEN.has(f.slug) && !hidden.has(f.slug))
    .map((f) => ({ slug: f.slug, label: f.displayName || f.slug, control: controlFor(f) }));

  return missing.length
    ? [...sections, { title: "Other", help: "Fields the panel has not been given a proper home for yet.", fields: missing }]
    : sections;
}

/**
 * The Image fields a collection's form marks `decorative: true`.
 *
 * `validateItem` does not ask for alt text on these, because the template
 * prints `alt=""` for them whatever is stored. Provider photographs are not
 * decorative and never will be.
 *
 * @param {string} collection
 * @param {Record<string, {fields: {slug: string, decorative?: boolean}[]}[]>} [forms]  the layouts to read; tests pass their own
 * @returns {string[]}
 */
export function decorativeFields(collection, forms = FORMS) {
  return (forms[collection] || []).flatMap((section) => section.fields.filter((f) => f.decorative === true).map((f) => f.slug));
}

/** A sensible control for a schema field the form does not mention. */
function controlFor(field) {
  switch (field.type) {
    case "RichText":
      return "rich";
    case "Image":
      return "image";
    case "Switch":
      return "switch";
    case "Number":
      return "number";
    case "Option":
      return "select";
    case "Reference":
      return "select";
    case "MultiReference":
      return "multiselect";
    case "DateTime":
      return "date";
    case "Link":
    case "VideoLink":
      return "url";
    default:
      return "text";
  }
}
